import { AppsyncRepositoryBase, handleError } from './appsyncRepositoryBase';

import { getSdk } from '@/infra/appsync';

/**
 * EntityPub用レポジトリの抽象クラス.
 */
export abstract class AppsyncRepository<
  E, P, J, PP, JP
> extends AppsyncRepositoryBase<E, P, J, PP, JP> {
  /**
   * Pub取得
   * @param id ID
   * @returns データ
   */
  async getPub(id: string) {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetPubEntity(
      () => sdk.GetPubEntity({
        entityName: this.entityName,
        entityKey: id,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntity;
    if (!resultEntity) {
      throw Error(`Error in ${this.entityName}/getPub: not found property`);
    }
    return resultEntity;
  }

  /**
   * Pubリスト取得
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns リスト
   */
  async getPubList(nextToken: string | null, limit = 25) {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetPubEntities(
      () => sdk.GetPubEntities({
        entityName: this.entityName,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntities;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getPubList: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => v,
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }

  /**
   * キーでPubリスト取得
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getPubListWithKey(key: string, nextToken: string | null, limit = 25) {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetPubEntitiesWithKey(
      () => sdk.GetPubEntitiesWithKey({
        entityName: this.entityName,
        key,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntitiesWithKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getPubListWithKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => v,
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }

  /**
   * サブキーでPubリスト取得
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getPubListWithSubKey(key: string, nextToken: string | null, limit = 25) {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetPubEntitiesWithSubKey(
      () => sdk.GetPubEntitiesWithSubKey({
        entityName: this.entityName,
        key,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntitiesWithSubKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getPubListWithSubKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => v,
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }

  /**
   * データ取得
   * @param id ID
   * @returns データ
   */
  async getData(id: string) {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetDataEntity(
      () => sdk.GetDataEntity({
        entityName: this.entityName,
        entityKey: id,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntity;
    if (!resultEntity) {
      throw Error(`Error in ${this.entityName}/getData: not found property`);
    }
    return resultEntity;
  }

  /**
   * データリスト取得
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getDataList(nextToken: string | null, limit = 25) {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetDataEntities(
      () => sdk.GetDataEntities({
        entityName: this.entityName,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntities;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getDataList: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => v,
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }

  /**
   * キーでデータリスト取得
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getDataListWithKey(key: string, nextToken: string | null, limit = 25) {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetDataEntitiesWithKey(
      () => sdk.GetDataEntitiesWithKey({
        entityName: this.entityName,
        key,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntitiesWithKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getDataListWithKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => v,
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }

  /**
   * サブキーでデータリスト取得
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getDataListWithSubKey(key: string, nextToken: string | null, limit = 25) {
    const client = await this.cognitoApi.getGraphQLClient();
    const sdk = getSdk(client);
    const result = await this.retryGetDataEntitiesWithSubKey(
      () => sdk.GetDataEntitiesWithSubKey({
        entityName: this.entityName,
        key,
        limit,
        nextToken,
      }),
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntitiesWithSubKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getDataListWithSubKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => v,
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }
}
